/**
 * Parses a markdown table string into structured data
 * @param markdown - The markdown table string
 * @returns An object with columns array and rows 2D array
 */
export function parseMarkdownTable(markdown: string): { columns: string[]; rows: string[][] } {
  if (!isValidMarkdownTable(markdown)) {
    throw new Error('Invalid markdown table format')
  }

  const lines = markdown
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)

  if (lines.length < 2) {
    throw new Error('Table must have at least a header and separator line')
  }

  // Parse header (first line)
  const headerLine = lines[0]
  const columns = parseTableRow(headerLine)

  // Skip separator line (second line)
  // Parse data rows (remaining lines)
  const rows: string[][] = []
  for (let i = 2; i < lines.length; i++) {
    const row = parseTableRow(lines[i])
    // Ensure row has same number of columns as header
    const normalizedRow: string[] = []
    for (let j = 0; j < columns.length; j++) {
      normalizedRow[j] = row[j] || ''
    }
    rows.push(normalizedRow)
  }

  return { columns, rows }
}

/**
 * Validates if a string is a valid markdown table
 * @param markdown - The markdown string to validate
 * @returns true if valid, false otherwise
 */
export function isValidMarkdownTable(markdown: string): boolean {
  if (!markdown || typeof markdown !== 'string') {
    return false
  }

  const lines = markdown
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)

  // Must have at least header + separator
  if (lines.length < 2) {
    return false
  }

  // Check header line (must start and end with |)
  const headerLine = lines[0]
  if (!headerLine.startsWith('|') || !headerLine.endsWith('|')) {
    return false
  }

  // Check separator line (must contain dashes)
  const separatorLine = lines[1]
  if (!separatorLine.startsWith('|') || !separatorLine.endsWith('|')) {
    return false
  }

  // Check if separator contains dashes in each column area
  const separatorCells = separatorLine.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0)
  const hasDashes = separatorCells.every(cell => /^-+$/.test(cell))
  if (!hasDashes || separatorCells.length === 0) {
    return false
  }

  // Check that header and separator have same number of columns
  const headerCells = headerLine.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0)
  if (headerCells.length !== separatorCells.length) {
    return false
  }

  // Check remaining rows (if any) have same structure
  for (let i = 2; i < lines.length; i++) {
    const rowLine = lines[i]
    if (!rowLine.startsWith('|') || !rowLine.endsWith('|')) {
      return false
    }
  }

  return true
}

/**
 * Helper function to parse a single table row
 * @param rowLine - A single line from the table
 * @returns Array of cell values (trimmed)
 */
function parseTableRow(rowLine: string): string[] {
  return rowLine
    .split('|')
    .slice(1, -1) // Remove first and last empty elements from split
    .map(cell => cell.trim())
}
